import React, { useCallback, useEffect, useRef, useState } from "react";
import { useScreenSizes } from "../WindowSizeUtils";

/**
 * トップページのお知らせバナー（自動切り替え）。
 * 表示内容は public/promoBanners.json を編集するだけで変更できます。
 * link が "/" で始まる場合はサイト内リンク、それ以外は外部リンクとして扱います。
 */

type PromoBanner = {
  title: string;
  text: string;
  imageSrc: string;
  imageAlt: string;
  buttonLabel: string;
  accentColor: string;
  link: string;
};

const SWITCH_INTERVAL = 6000; // 切り替え間隔（ミリ秒）
const FADE_DURATION = 400; // フェードにかける時間（ミリ秒）

const isExternalLink = (link: string) => !link.startsWith("/");

const resolveLink = (link: string) =>
  isExternalLink(link) ? link : `${process.env.PUBLIC_URL}${link}`;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const PromoCarousel: React.FC = () => {
  const { isSmallScreen, isMobileScreen } = useScreenSizes();

  const [banners, setBanners] = useState<PromoBanner[]>([]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [paused, setPaused] = useState(false);

  // フェード中のタイマーを片付けるための参照
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetch(`${process.env.PUBLIC_URL}/promoBanners.json`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`promoBanners.json の取得に失敗しました (${response.status})`);
        }
        return response.json();
      })
      .then((data: PromoBanner[]) => {
        if (!cancelled && Array.isArray(data)) {
          setBanners(data);
        }
      })
      .catch((error) => {
        // バナーが読み込めなくてもトップページ自体は壊さない
        console.error(error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // フェードアウト → 差し替え → フェードイン
  const goTo = useCallback(
    (next: number) => {
      if (banners.length === 0) return;

      const target = (next + banners.length) % banners.length;

      if (prefersReducedMotion()) {
        setIndex(target);
        setVisible(true);
        return;
      }

      setVisible(false);
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
      fadeTimer.current = setTimeout(() => {
        setIndex(target);
        setVisible(true);
      }, FADE_DURATION);
    },
    [banners.length]
  );

  // 自動切り替え
  useEffect(() => {
    if (paused || banners.length <= 1) return;

    const timer = setInterval(() => {
      goTo(index + 1);
    }, SWITCH_INTERVAL);

    return () => clearInterval(timer);
  }, [paused, banners.length, index, goTo]);

  // アンマウント時にフェード用タイマーを止める
  useEffect(() => {
    return () => {
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
    };
  }, []);

  if (banners.length === 0) return null;

  const banner = banners[index];
  const external = isExternalLink(banner.link);

  const arrowButtonStyle: React.CSSProperties = {
    background: "rgba(255, 255, 255, 0.85)",
    border: "2px solid #691C70",
    color: "#691C70",
    borderRadius: "50%",
    width: isMobileScreen ? "28px" : "36px",
    height: isMobileScreen ? "28px" : "36px",
    fontSize: isMobileScreen ? "14pt" : "18pt",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
    flexShrink: 0,
  };

  return (
    <section
      aria-label="お知らせ"
      aria-roledescription="カルーセル"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      style={{
        margin: "50px 10%",
        position: "relative",
        border: "2px solid black",
        borderRadius: "30px",
        padding: isMobileScreen ? "30px 15px 20px" : "35px 25px 25px",
      }}
    >
      <h2
        style={{
          fontFamily: "Noto Sans JP",
          fontSize: isMobileScreen ? "18pt" : isSmallScreen ? "20pt" : "24pt",
          margin: 0,
          backgroundColor: "white",
          position: "absolute",
          top: "-1em",
          left: "1em",
          padding: "0 10px",
        }}
      >
        お知らせ
      </h2>

      <div style={{ display: "flex", alignItems: "center", gap: isMobileScreen ? "6px" : "12px" }}>
        <button
          type="button"
          onClick={() => goTo(index - 1)}
          aria-label="前のお知らせ"
          style={arrowButtonStyle}
        >
          ‹
        </button>

        <a
          href={resolveLink(banner.link)}
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          aria-label={`${banner.title} — ${banner.buttonLabel}${external ? "（外部サイト）" : ""}`}
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            alignItems: "center",
            gap: isMobileScreen ? "10px" : "20px",
            textDecoration: "none",
            color: "black",
            opacity: visible ? 1 : 0,
            transition: `opacity ${FADE_DURATION}ms ease-in-out`,
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/${banner.imageSrc}`}
            alt={banner.imageAlt}
            style={{
              width: isMobileScreen ? "100%" : isSmallScreen ? "80%" : "320px",
              height: isMobileScreen ? "140px" : isSmallScreen ? "180px" : "200px",
              objectFit: "cover",
              borderRadius: "15px",
              flexShrink: 0,
            }}
          />

          <div style={{ textAlign: "left", width: "100%" }}>
            <p
              style={{
                fontFamily: "Noto Sans JP",
                fontWeight: 600,
                fontSize: isMobileScreen ? "13pt" : isSmallScreen ? "15pt" : "18pt",
                margin: "0 0 10px 0",
                color: banner.accentColor,
              }}
            >
              {banner.title}
            </p>
            <p
              style={{
                fontFamily: "Noto Sans JP",
                fontSize: isMobileScreen ? "10pt" : isSmallScreen ? "11pt" : "14pt",
                lineHeight: 1.6,
                margin: "0 0 15px 0",
              }}
            >
              {banner.text}
            </p>
            <span
              style={{
                display: "inline-block",
                fontFamily: "Noto Sans JP",
                fontSize: isMobileScreen ? "9pt" : isSmallScreen ? "11pt" : "13pt",
                color: banner.accentColor,
                border: `2px solid ${banner.accentColor}`,
                borderRadius: "16px",
                padding: isMobileScreen ? "3px 12px" : "5px 20px",
              }}
            >
              {banner.buttonLabel}
              {external ? " ↗" : ""}
            </span>
          </div>
        </a>

        <button
          type="button"
          onClick={() => goTo(index + 1)}
          aria-label="次のお知らせ"
          style={arrowButtonStyle}
        >
          ›
        </button>
      </div>

      {/* 何枚目かを示すドット */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "10px",
          marginTop: "15px",
        }}
      >
        {banners.map((item, dotIndex) => (
          <button
            key={item.title}
            type="button"
            onClick={() => goTo(dotIndex)}
            aria-label={`${dotIndex + 1}枚目のお知らせ：${item.title}`}
            aria-current={dotIndex === index}
            style={{
              width: "12px",
              height: "12px",
              padding: 0,
              borderRadius: "50%",
              border: "2px solid #691C70",
              backgroundColor: dotIndex === index ? "#691C70" : "transparent",
              cursor: "pointer",
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default PromoCarousel;
